import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabaseClient";
import type { Job } from "@/pages/admin/jobs/components/JobModal";

type Message = {
  id: string;
  tenant_id: string;
  client_id: string;
  sender_type: string;
  message: string;
  is_read: boolean;
  created_at: string;
};

interface Props {
  job: Job;
  tenantId: string;
}

export default function MessagesTab({ job, tenantId }: Props) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("client_messages")
      .select("*")
      .eq("client_id", job.client_id)
      .eq("tenant_id", tenantId)
      .order("created_at", { ascending: true });
    setMessages((data ?? []) as Message[]);
    setLoading(false);
  }, [job.client_id, tenantId]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  useEffect(() => {
    const unread = messages.filter((m) => !m.is_read && m.sender_type === "client");
    if (unread.length === 0) return;
    const ids = unread.map((m) => m.id);
    supabase.from("client_messages").update({ is_read: true }).in("id", ids).eq("tenant_id", tenantId);
    setMessages((prev) => prev.map((m) => (ids.includes(m.id) ? { ...m, is_read: true } : m)));
  }, [messages, tenantId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setSending(true);
    const { data, error } = await supabase
      .from("client_messages")
      .insert({
        tenant_id:   tenantId,
        client_id:   job.client_id,
        sender_type: "studio",
        message:     newMessage.trim(),
        is_read:     false,
      })
      .select()
      .maybeSingle();
    if (!error && data) {
      setMessages((prev) => [...prev, data as Message]);
      setNewMessage("");
    }
    setSending(false);
  };

  const formatTime = (d: string) => {
    const date = new Date(d);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const unreadCount = messages.filter((m) => !m.is_read && m.sender_type === "client").length;

  return (
    <div className="flex flex-col h-[600px] bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C8A96E]/10">
            <i className="ri-message-3-line text-[#C8A96E] text-sm"></i>
          </div>
          <div>
            <p className="text-[#0f172a] text-sm font-medium">Client Messages</p>
            <p className="text-slate-400 text-xs">Messages with this client</p>
          </div>
        </div>
        {unreadCount > 0 && (
          <span className="bg-[#C8A96E] text-white text-xs font-bold px-2 py-0.5 rounded-full">
            {unreadCount} new
          </span>
        )}
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-[#f3f7fb]">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-6 h-6 border-2 border-[#C8A96E] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-slate-200">
              <i className="ri-message-3-line text-slate-400 text-xl"></i>
            </div>
            <p className="text-slate-400 text-sm">No messages yet</p>
            <p className="text-slate-300 text-xs">Start the conversation with your client</p>
          </div>
        ) : (
          <>
            {messages.map((msg, idx) => {
              const isStudio = msg.sender_type === "studio";
              const prevMsg = idx > 0 ? messages[idx - 1] : null;
              const showDateSep = !prevMsg || new Date(msg.created_at).toDateString() !== new Date(prevMsg.created_at).toDateString();

              return (
                <div key={msg.id}>
                  {showDateSep && (
                    <div className="flex items-center gap-3 my-3">
                      <div className="flex-1 h-px bg-slate-200" />
                      <span className="text-slate-400 text-xs whitespace-nowrap">
                        {new Date(msg.created_at).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                      </span>
                      <div className="flex-1 h-px bg-slate-200" />
                    </div>
                  )}
                  <div className={`flex ${isStudio ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[70%] ${isStudio ? "items-end" : "items-start"} flex flex-col gap-1`}>
                      {!isStudio && (
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <div className="w-5 h-5 flex items-center justify-center rounded-full bg-[#C8A96E]/15 flex-shrink-0">
                            <i className="ri-user-line text-[#C8A96E] text-[10px]"></i>
                          </div>
                          <span className="text-slate-400 text-xs">Client</span>
                        </div>
                      )}
                      <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                        isStudio
                          ? "bg-[#C8A96E] text-white rounded-br-sm"
                          : "bg-white text-[#0f172a] rounded-bl-sm border border-slate-200"
                      }`}>
                        {msg.message}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-slate-300 text-[10px]">{formatTime(msg.created_at)}</span>
                        {isStudio && (
                          <span className={`text-[10px] ${msg.is_read ? "text-emerald-500" : "text-slate-300"}`}>
                            {msg.is_read ? "Read" : "Sent"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </>
        )}
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="flex items-end gap-3 px-5 py-4 border-t border-slate-100 flex-shrink-0 bg-white">
        <textarea
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(e); }
          }}
          placeholder="Type a message… (Enter to send)"
          rows={1}
          maxLength={500}
          className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors resize-none"
          style={{ minHeight: "42px", maxHeight: "120px" }}
        />
        <button
          type="submit"
          disabled={!newMessage.trim() || sending}
          className="w-10 h-10 flex items-center justify-center bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-40 text-white rounded-xl transition-colors cursor-pointer flex-shrink-0"
        >
          {sending
            ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            : <i className="ri-send-plane-fill text-sm"></i>
          }
        </button>
      </form>
    </div>
  );
}